// MARK:- OS 2.0 PDP "Product related" slider — fetches Shopify's recommendations
// API when the section's data_source is "product_recommendations" and replaces
// the server-rendered fallback (which uses the chosen fallback collection) with
// real recommendations.
//
// File naming: standalone module in this theme uses the `component-` file-name
// prefix so all standalone components sort together in `assets/` and grep nicely.
//
// Architecture: hand-edited asset, NOT a webpack/gulp build output. Loaded as an
// ES module from sections/product-related.liquid:
//
//   <script src="{{ 'component-product-related.js' | asset_url }}" type="module"></script>
//
// `type="module"` deduplicates by URL so multiple instances on the page only
// fetch + execute it once.
//
// Markup contract (set in sections/product-related.liquid):
//   <section data-as-product-related
//            data-product-id="123"
//            data-section-id="<section.id>"
//            data-data-source="product_recommendations"
//            data-products-to-show="4">
//     ...slider rendered with fallback collection...
//   </section>
//
// Flow:
// 1. Server-side, the section renders with the chosen fallback collection's
//    products. Splide initializes via the legacy `productCardSlider()` jQuery
//    handler in src/js/theme.js (catches `.product-card-slider.splide`).
// 2. On DOMContentLoaded, this module finds sections with
//    data-data-source="product_recommendations" and fetches:
//      /recommendations/products?section_id=<sid>&product_id=<pid>&limit=<n>&intent=related
//    The response is the section's HTML re-rendered with `recommendations.products`
//    populated.
// 3. Extract the inner content, replace the section's innerHTML, then mount a
//    fresh Splide on the new slider (the legacy jQuery init has already passed —
//    won't pick up the swapped DOM on its own).

// MARK:- perPage:1 (not 2) — autoWidth:true + perPage:2 caps the last reachable index at `length - 2`, leaving the last slide partly cut off. See productCardSlider() in src/js/theme.js for the same fix.
const SPLIDE_CONFIG = {
  type: 'slide',
  speed: 300,
  perPage: 1,
  perMove: 1,
  autoWidth: true,
  flickPower: 300,
  arrows: false,
  pagination: false,
};

async function refreshSection(section) {
  const productId = section.dataset.productId;
  const sectionId = section.dataset.sectionId;
  const limit = section.dataset.productsToShow || 4;

  if (!productId || !sectionId) return;

  const url = `/recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=related`;

  let response;
  try {
    response = await fetch(url);
  } catch (err) {
    return;
  }
  if (!response.ok) return;

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-as-product-related]');
  if (!fresh) return;

  // Bail if the API returned the same fallback content (no real recommendations
  // available for this product yet) — leaves the legacy Splide instance intact.
  if (fresh.innerHTML.trim() === section.innerHTML.trim()) return;

  section.innerHTML = fresh.innerHTML;

  if (typeof window.Splide !== 'undefined') {
    section.querySelectorAll('.product-card-slider.splide').forEach((slider) => {
      new window.Splide(slider, SPLIDE_CONFIG).mount();
    });
  }
}

function init() {
  const sections = document.querySelectorAll('[data-as-product-related]');
  sections.forEach((section) => {
    if (section.dataset.dataSource !== 'product_recommendations') return;
    refreshSection(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
